// WAN und Rechner -- EIN gemeinsamer Stand für cluster-stats (Cluster-Seite,
// Dashboard-Karte, Alarme), wan-summary (Internet-Karten) und hosts-summary
// (Rechner-Karte).
//
// Datenfluss (seit 03.11.2026 über das Homelab-Protokoll, docs/protokoll.md
// › „WAN und Rechner über das Protokoll“): jeder Rechner der Cluster-
// Konfiguration ist eine Instanz host.<id>.stand (Eintrag, online seit, Updates,
// SSH, Aufenthalt) und host.<id>.stats (laufende Werte: CPU, Temperatur, Speicher,
// Strom, WLAN); die Cluster in ihrer Reihenfolge, die Anomalien gegen die
// Baseline und die Rechner, die offline sein dürfen, stehen in host.lage; das
// Internet (Probe hinter forge und je Router, was sein Agent meldet) in wan.lage.
// Das Backend gleicht die Quellen ab (Cluster-Monitor, WAN-Prober, Baselines,
// Overrides); der Hub verwirft Unverändertes und schickt Deltas. Bis dahin holte
// jeder Tab /v2-api/cluster/stats und /v2-api/wan/summary alle 30 s, dazu bei
// jedem WS-Signal ("cluster:stats" bis zu 1/s mit dem ganzen Snapshot, 4,8 kB je
// Rahmen; "wan"); hosts-summary holte noch baselines und overrides dazu.
//   - Hier stehen die einzigen Abo-Stellen (`host.*.stand`, `host.*.stats`,
//     `host.lage`, `wan.lage`). Aus einem Stand baut lib/rechner-protokoll.ts die
//     REST-Form seiner Quelle (`ClusterStats` mit `ClusterStatsHost`, `WanStatus`).
//     Die Rechner einer Antwort zerlegt dieser Store: der Eintrag steht in der
//     Liste, die laufenden Werte (`stats`) daneben -- sie kommen aus eigener
//     Instanz --, `getClusterStatsAusRechner()` setzt beides wieder zusammen.
//   - REST (`/v2-api/cluster/stats`, `/v2-api/wan/summary`, je für sich) nur beim
//     Start, als Revalidator (Reconnect, Tab kommt zurück) und als Neuversuch 15 s
//     nach einem Fehlschlag -- jeweils nur die gescheiterten Teile --, dazu in
//     refreshRechner (nach einer Aktion). KEIN Takt, KEIN Abo auf die alten
//     WS-Kanäle "cluster:stats" und "wan" mehr. (Das Präsenz-Abo "hosts:live" der
//     Seiten bleibt: es taktet die Agenten hoch, nicht diesen Store.) Für host.lage
//     gibt es kein REST: die Lage kommt mit dem ersten Bündel der Abos, bis dahin
//     liefert getHostLage() `undefined`. Die Cluster-Liste (id, name) steht auch in
//     der Antwort von cluster/stats und füllt die Cluster, bis die Lage da ist.
//   - Stände und Abrufe mischen wie in agents-summary (ProtokollListe): Stände
//     in-place, ein Stand während eines Abrufs liegt über dessen Antwort, ein
//     `weg` hinterlässt einen Grabstein. Die Stelle eines Rechners steht in
//     `cluster` und `position` (Index im Cluster; aus der Antwort ihr Index). Die
//     laufenden Werte und das Internet sind Einzelwerte je Rechner bzw. im Ganzen:
//     ein Stand während eines Abrufs liegt ebenfalls darüber (laufende Nummer).
//     Jeder Teil gilt für sich als bekannt (Ablage, Abruf oder Stand): vorher
//     liefert sein Getter `undefined`.
//
// Mehrere Stores teilen sich den Stand: startRechner() zählt mit und liefert die
// Abmeldung; der letzte, der geht, schließt ab.
//
// SWR: ja -- Rechner, Laufwerte, Cluster-Liste und Internet kommen beim Start aus
// der Ablage (ein Schlüssel `rechner`), gedrosselt geschrieben (höchstens alle
// 2 s, nicht bei jedem Rahmen). Die Lage nicht: Anomalien und Ausnahmen sind
// Stand des Augenblicks; hosts-summary hat seine eigene Ablage.

import { SvelteMap } from "svelte/reactivity";

import { getJSON } from "./api";
import { clearPersisted, loadPersisted, persist } from "./persistent-summary.svelte";
import { ProtokollListe } from "./protokoll-liste.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { HostLage, HostStand, HostStats, WanLage } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import {
  clusterListeAusProtokoll,
  clusterStatsAus,
  hostAusProtokoll,
  hostIdAusStandInstanz,
  hostIdAusStatsInstanz,
  positionAusStand,
  statsAusProtokoll,
  wanAusProtokoll,
  type HostEintrag,
} from "./rechner-protokoll";
import { registerRevalidator } from "./revalidate.svelte";
import { gleich } from "./storage-protokoll";
import type { WanStatus } from "./netzwerk/netz";
import type { ClusterStats, ClusterStatsHost, ClusterStatsHostStats } from "./types";

const STORAGE_KEY = "rechner";

// Die Ablage-Schlüssel der alten Stores (bis 03.11.2026): sie werden nicht mehr
// geschrieben; startRechner räumt sie einmal je Sitzung weg.
const ALTE_SCHLUESSEL = ["cluster-stats", "wan"];
let _altesGeraeumt = false;

/** Ein Cluster der Liste: Kennung und Name. */
interface ClusterRef {
  id: string;
  name: string;
}

/** Die Ablage: ein Teil fehlt, solange er unbekannt war. */
interface Gespeichert {
  cluster?: ClusterRef[];
  hosts?: HostEintrag[];
  stats?: Record<string, ClusterStatsHostStats>;
  wan?: WanStatus;
}

/** Die Teile des gemeinsamen Stands; jeder hat seinen Endpunkt. */
export type RechnerTeil = "hosts" | "wan";
const ALLE_TEILE: RechnerTeil[] = ["hosts", "wan"];

/** Ein Rechner ohne bekannte Stelle kommt ans Ende seines Clusters. */
const POSITION_UNBEKANNT = 1_000_000;

function vergleicheHosts(a: HostEintrag, b: HostEintrag): number {
  if (a.cluster !== b.cluster) return a.cluster < b.cluster ? -1 : 1;
  if (a.position !== b.position) return a.position < b.position ? -1 : 1;
  return a.id === b.id ? 0 : a.id < b.id ? -1 : 1;
}

// Die Rechner (Einträge ohne `stats`), ob die Liste etwas weiß (Ablage, Abruf
// oder Stand: vorher liefert ihr Getter `undefined`).
const _hosts = new ProtokollListe<HostEintrag>((e) => e.id, vergleicheHosts);
let _hostsBekannt = $state(false);

// Die laufenden Werte je Rechner mit der laufenden Nummer ihres jüngsten Stands
// (kam er während eines Abrufs, liegt er über dessen Antwort).
interface StatsEintrag {
  s: ClusterStatsHostStats;
  nr: number;
}
const _stats = new SvelteMap<string, StatsEintrag>();
let _statsNr = 0;
// Grabsteine für `weg` der laufenden Werte: ein Abruf, der schon lief, bringt sie nicht zurück.
const _statsWeg = new Map<string, number>();

// Die Lage (aus dem Protokoll), die Cluster-Liste aus der Antwort von
// cluster/stats (bis die Lage da ist) und das Internet -- das mit der laufenden
// Nummer seines jüngsten Stands. `undefined` = unbekannt.
let _lage = $state.raw<HostLage | undefined>(undefined);
let _clusterRest = $state.raw<ClusterRef[] | undefined>(undefined);
let _wan = $state.raw<WanStatus | undefined>(undefined);
let _wanNr = 0;
let _nr = 0;

let _nutzer = 0;
let _unsub: Array<() => void> = [];

// ── Persistenz ───────────────────────────────────────────────────────

// Aus dem Protokoll kommt oft etwas (die Laufwerte im Sekundentakt): gespeichert
// wird gebündelt, höchstens alle 2 s.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    const hosts = _hostsBekannt ? $state.snapshot(_hosts.items) : undefined;
    const ids = new Set(hosts?.map((h) => h.id));
    const g: Gespeichert = {
      cluster: _lage ? clusterListeAusProtokoll(_lage) : _clusterRest,
      hosts,
      stats: hosts ? Object.fromEntries([..._stats].filter(([id]) => ids.has(id)).map(([id, e]) => [id, e.s])) : undefined,
      wan: _wan,
    };
    persist(STORAGE_KEY, g);
  }, 2_000);
}

// ── Stände ──────────────────────────────────────────────────────────

function beiHostStand(instanz: string, daten: Record<string, unknown>): void {
  const id = hostIdAusStandInstanz(instanz);
  if (id === undefined) return;
  const st = skaliere<HostStand>("host.stand", daten);
  const eintrag: HostEintrag = {
    ...hostAusProtokoll(st, undefined, undefined, id),
    cluster: st.cluster ?? "",
    position: positionAusStand(st) ?? POSITION_UNBEKANNT,
  };
  // Die Kennung der Instanz gilt (der Adapter setzt beide gleich).
  eintrag.id = id;
  const geaendert = _hosts.stand(eintrag);
  // Vor dem ersten Abruf legt der Abruf die Einträge über die Antwort.
  if (_hosts.bereit) _hostsBekannt = true;
  if (geaendert) persistSpaeter();
}

function beiHostStandWeg(instanz: string): void {
  const id = hostIdAusStandInstanz(instanz);
  if (id !== undefined && _hosts.weg(id)) persistSpaeter();
}

function beiHostStats(instanz: string, daten: Record<string, unknown>, meta: { z: number }): void {
  const id = hostIdAusStatsInstanz(instanz);
  if (id === undefined) return;
  const st = skaliere<HostStats>("host.stats", daten);
  _statsWeg.delete(id);
  _stats.set(id, { s: statsAusProtokoll(st, meta.z), nr: ++_statsNr });
  persistSpaeter();
}

function beiHostStatsWeg(instanz: string): void {
  const id = hostIdAusStatsInstanz(instanz);
  if (id === undefined) return;
  _statsWeg.set(id, ++_statsNr);
  if (_stats.delete(id)) persistSpaeter();
}

function beiLage(_instanz: string, daten: Record<string, unknown>): void {
  const l = skaliere<HostLage>("host.lage", daten);
  if (_lage && gleich(_lage, l)) return;
  _lage = l;
  persistSpaeter();
}

function beiWan(_instanz: string, daten: Record<string, unknown>): void {
  _wanNr = ++_nr;
  setzeWan(wanAusProtokoll(skaliere<WanLage>("wan.lage", daten)));
}

// Das Internet nur setzen, wenn es sich ändert (sonst meldet jeder Stand ein Update).
function setzeWan(neu: WanStatus): void {
  if (_wan && gleich(_wan, neu)) return;
  _wan = neu;
  persistSpaeter();
}

// ── REST ─────────────────────────────────────────────────────────────

// Nach einem gescheiterten Abruf ein neuer Versuch: ohne Takt käme sonst erst
// der nächste Revalidator -- und bis dahin bliebe ein aus der Ablage geholter,
// inzwischen verschwundener Rechner stehen (für den kommt kein `weg`, diese
// Sitzung kannte seine Instanz nie).
const NEUVERSUCH_MS = 15_000;
let _neuversuch: ReturnType<typeof setTimeout> | undefined;
// Die Teile, deren letzter Abruf scheiterte: nur sie versucht der Neuversuch.
const _gescheitert = new Set<RechnerTeil>();

// Lauf: zählt Start und Stopp hoch. Eine Antwort, die erst nach dem Stopp
// (oder nach einem neuen Start) ankommt, gehört zu keinem laufenden Store.
let _lauf = 0;

/** Die Antwort eines Endpunkts, `undefined` bei Fehlschlag. */
async function hole<T>(pfad: string): Promise<T | undefined> {
  try {
    return await getJSON<T>(pfad);
  } catch {
    return undefined;
  }
}

async function ladeHosts(lauf: number): Promise<boolean> {
  const ab = _hosts.nr;
  const abStats = _statsNr;
  const rest = await hole<ClusterStats[] | null>("/v2-api/cluster/stats");
  if (lauf !== _lauf) return true;
  // Eine leere Antwort (`null`) ist eine leere Liste, ein Fehlschlag `undefined`.
  const antwort = rest === undefined ? undefined : (rest ?? []);
  let eintraege: HostEintrag[] | undefined;
  if (antwort) {
    const cluster = antwort.map((c) => ({ id: c.id, name: c.name }));
    if (!_clusterRest || !gleich(_clusterRest, cluster)) {
      _clusterRest = cluster;
      persistSpaeter();
    }
    const liste: HostEintrag[] = [];
    for (const c of antwort) {
      const hosts = c.hosts ?? [];
      for (let i = 0; i < hosts.length; i++) {
        const { stats, ...eintrag } = hosts[i];
        liste.push({ ...eintrag, cluster: c.id, position: i });
        // Laufende Werte: ein Stand während des Abrufs liegt darüber.
        if (stats && (_stats.get(eintrag.id)?.nr ?? _statsWeg.get(eintrag.id) ?? 0) <= abStats) _stats.set(eintrag.id, { s: stats, nr: ++_statsNr });
      }
    }
    eintraege = liste;
  }
  try {
    if (_hosts.mische(eintraege, ab)) persistSpaeter();
    if (eintraege || _hosts.neuer(ab)) _hostsBekannt = true;
  } finally {
    _hosts.bereit = true;
  }
  return antwort !== undefined;
}

async function ladeWan(lauf: number): Promise<boolean> {
  const ab = _wanNr;
  const rest = await hole<WanStatus>("/v2-api/wan/summary");
  if (lauf !== _lauf) return true;
  // Ein Stand während des Abrufs liegt darüber. `routers` kann in der Antwort
  // `null` sein (Go: leere Liste), im Stand fehlt die Liste dann -- gleiche Form.
  if (rest && _wanNr <= ab) setzeWan({ ...rest, routers: rest.routers ?? [] });
  return rest !== undefined;
}

const _laden: Record<RechnerTeil, (lauf: number) => Promise<boolean>> = {
  hosts: ladeHosts,
  wan: ladeWan,
};

// Holt die genannten Teile (Standard: alle) und mischt jeden für sich.
async function ladeTeile(teile: RechnerTeil[] = ALLE_TEILE): Promise<void> {
  const lauf = _lauf;
  // Die Teile getrennt: scheitert einer, gilt der andere trotzdem.
  const antworten = await Promise.allSettled(teile.map((t) => _laden[t](lauf)));
  if (lauf !== _lauf) return;
  teile.forEach((t, i) => {
    const a = antworten[i];
    if (a.status === "fulfilled" && a.value) _gescheitert.delete(t);
    else _gescheitert.add(t);
  });
  if (_gescheitert.size > 0 && _nutzer > 0 && !_neuversuch) {
    _neuversuch = setTimeout(() => {
      _neuversuch = undefined;
      if (_nutzer > 0) void ladeTeile([..._gescheitert]);
    }, NEUVERSUCH_MS);
  }
}

/** Meldet einen Nutzer des gemeinsamen Stands an (der erste startet Abo und
 *  Abruf) und liefert die Abmeldung (der letzte schließt ab). */
export function startRechner(): () => void {
  _nutzer++;
  if (_nutzer === 1) {
    _lauf++;
    // SWR: gecachten Stand sofort einspielen. Das Protokoll bringt die frischen
    // Werte danach in Sekunden, der Cache deckt nur die First-Paint-Lücke.
    if (!_altesGeraeumt) {
      _altesGeraeumt = true;
      for (const k of ALTE_SCHLUESSEL) clearPersisted(k);
    }
    const cached = loadPersisted<Gespeichert>(STORAGE_KEY);
    if (cached) {
      if (cached.cluster) _clusterRest = cached.cluster;
      if (cached.hosts) {
        _hosts.items = cached.hosts;
        _hostsBekannt = true;
      }
      for (const [id, s] of Object.entries(cached.stats ?? {})) _stats.set(id, { s, nr: 0 });
      if (cached.wan) _wan = cached.wan;
    }
    void ladeTeile();
    _unsub.push(
      abonniere("host.*.stand", { rate: "normal" }, { stand: beiHostStand, weg: beiHostStandWeg }),
      abonniere("host.*.stats", { rate: "normal" }, { stand: beiHostStats, weg: beiHostStatsWeg }),
      abonniere("host.lage", { rate: "normal" }, { stand: beiLage }),
      abonniere("wan.lage", { rate: "normal" }, { stand: beiWan }),
    );
    // ⚠️ Der Protokoll-Kanal allein reicht NICHT. Reißt die Leitung ab -- und
    // jeder Deploy tut das --, sind verpasste Löschungen weg. registerRevalidator
    // holt beim Reconnect und beim Zurückkommen des Tabs alles frisch.
    _unsub.push(registerRevalidator(() => void ladeTeile()));
  }
  let aktiv = true;
  return () => {
    if (!aktiv) return;
    aktiv = false;
    _nutzer--;
    if (_nutzer === 0) stoppe();
  };
}

function stoppe(): void {
  _lauf++;
  for (const off of _unsub) off();
  _unsub = [];
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = undefined;
  }
  if (_neuversuch) {
    clearTimeout(_neuversuch);
    _neuversuch = undefined;
  }
  _gescheitert.clear();
  _hosts.leere();
  _hostsBekannt = false;
  _stats.clear();
  _statsWeg.clear();
  _statsNr = 0;
  _lage = undefined;
  _clusterRest = undefined;
  _wan = undefined;
  _wanNr = 0;
  _nr = 0;
  _vorher = undefined;
}

// ── Getter ───────────────────────────────────────────────────────────

// Die Rechner eines Aufrufs behalten ihre Identität, solange sich nichts an
// ihnen ändert (ein Stand mit neuer CPU-Last zieht sonst jede Zeile neu).
function stabil(next: ClusterStats[], vorher: ClusterStats[] | undefined): ClusterStats[] {
  if (!vorher) return next;
  const alteHosts = new Map<string, ClusterStatsHost>(vorher.flatMap((c) => c.hosts.map((h) => [h.id, h] as const)));
  const alteCluster = new Map(vorher.map((c) => [c.id, c]));
  const out = next.map((c) => {
    const hosts = c.hosts.map((h) => {
      const alt = alteHosts.get(h.id);
      return alt && gleich(alt, h) ? alt : h;
    });
    const alt = alteCluster.get(c.id);
    if (alt && alt.name === c.name && alt.hosts.length === hosts.length && alt.hosts.every((h, i) => h === hosts[i])) return alt;
    return { ...c, hosts };
  });
  return vorher.length === out.length && out.every((c, i) => c === vorher[i]) ? vorher : out;
}

let _vorher: ClusterStats[] | undefined;

// Die Cluster mit ihren Rechnern samt laufenden Werten; vorhanden, sobald
// Rechner und Cluster-Liste bekannt sind. Neu gerechnet nur bei geänderter Eingabe.
const _clusterStats = $derived.by((): ClusterStats[] | undefined => {
  if (!_hostsBekannt) return undefined;
  const lage = _lage ?? (_clusterRest ? { cluster: _clusterRest } : undefined);
  if (!lage) return undefined;
  const hosts = new Map<string, HostEintrag>();
  for (const e of _hosts.items) {
    const s = _stats.get(e.id)?.s;
    hosts.set(e.id, s ? { ...e, stats: s } : { ...e });
  }
  const next = stabil(clusterStatsAus(lage, hosts), _vorher);
  _vorher = next;
  return next;
});

/** Die Cluster mit Rechnern und laufenden Werten (wie `GET /v2-api/cluster/stats`);
 *  `undefined`, solange weder Ablage noch Abruf noch Protokoll etwas geliefert haben. */
export function getClusterStatsAusRechner(): ClusterStats[] | undefined {
  return _clusterStats;
}

/** Das Internet je Router (wie `GET /v2-api/wan/summary`); `undefined`, solange nichts bekannt ist. */
export function getWanAusRechner(): WanStatus | undefined {
  return _wan;
}

/** Die Lage der Rechner (Cluster-Reihenfolge, Anomalien, erlaubte Ausfälle,
 *  Verlauf-Rechner) aus `host.lage`; `undefined`, bis das Protokoll sie geliefert hat. */
export function getHostLage(): HostLage | undefined {
  return _lage;
}

/** Holt die genannten Teile sofort per REST -- nach einer Aktion, deren Ergebnis
 *  die Seite gleich zeigen soll (das Protokoll braucht einen Moment). */
export function refreshRechner(teile: RechnerTeil[]): Promise<void> {
  // Ohne Nutzer (Seite verlassen, Aktion endet später) nichts mehr füllen.
  if (_nutzer === 0) return Promise.resolve();
  return ladeTeile(teile);
}
